import { findStation } from '../domain/stations';
import type { MediaSessionPort } from '../ports/media-session';
import { type AppState, playableStations } from './app';

/**
 * Most iz store v medijsko sejo — zaklenjen zaslon, CarPlay, Bluetooth glava,
 * obvestilo Androida.
 *
 * Obstaja, ker je bil `MediaSessionPort` za to zgrajen, kompozicija pa ga ni
 * nikoli povezala: edino klicno mesto `setNowPlaying` je bilo
 * `startPlayback` (`domain/playback.ts`) z golim imenom postaje, zato so bile
 * veje za `artist` in `artworkUrl` v adapterju v produkciji mrtve. Na napravi
 * se je to videlo kot CarPlay z ikono aplikacije namesto ovitka in brez imena
 * skladbe (potrjeno 2026-07-20).
 *
 * Kaskada metapodatkov ostane nedotaknjena — skladbo in ovitek **že** razreši
 * in zapiše v store (`store/nowplaying.ts`). Manjkal je samo prenos naprej.
 *
 * ⚠️ Meja iz T1 §3 ni kršena: most govori z `MediaSessionPort`, ne z
 * `navigator.mediaSession`. Ta ostaja samo v `src/adapters/audio/`.
 */

export interface MediaMetadataBridgeDeps {
  /** `null`, kadar brskalnik medijske seje nima — most takrat ne dela nič. */
  session: MediaSessionPort | null;
}

export interface MediaMetadataBridge {
  /** Kliče se ob vsaki spremembi stanja; sam ugotovi, ali je kaj za poslati. */
  sync(state: AppState): void;
}

/**
 * Podpis tega, kar je sistem nazadnje dobil.
 *
 * Nov `MediaMetadata` ob vsakem tiku store bi zaklenjen zaslon utripal in
 * ovitek ponovno nalagal. Ključ zato nosi natanko tista polja, ki gredo ven —
 * sprememba česarkoli drugega v stanju sistema ne zadeva.
 */
function signature(title: string, artist: string | null, artworkUrl: string | null): string {
  // Ločilo je NUL, ker se v nobenem od treh polj ne more pojaviti — presledek
  // bi »A B« brez izvajalca in »A« z izvajalcem »B« zlil v isti ključ.
  //
  // ⚠️ Zapisano kot ubežno zaporedje, NE kot dobesedni bajt: dobesedni NUL
  // naredi datoteko binarno in ripgrep jo pri iskanju po vsebini tiho preskoči.
  return [title, artist ?? '', artworkUrl ?? ''].join('\u0000');
}

export function createMediaMetadataBridge(deps: MediaMetadataBridgeDeps): MediaMetadataBridge {
  const { session } = deps;

  let last: string | null = null;

  return {
    sync(state) {
      if (session === null) return;

      // Vključno s predposluhom: zaklenjen zaslon in CarPlay morata ime
      // postaje pokazati tudi takrat, kadar ta še ni v seznamu (P2 §2.4).
      const station = findStation(playableStations(state), state.stationId);
      if (station === null) {
        // Brez izbrane postaje seja nima česa kazati. Prazen zapis pošljemo
        // enkrat, ne ob vsakem tiku.
        if (last === null) return;
        last = null;
        session.setNowPlaying(null);
        return;
      }

      const track = state.track;
      // Kadar skladbe ni — govorne postaje, marsikateri Icecast (T3 §3) — ime
      // postaje prevzame naslov. To je pogosto in legitimno stanje, ne napaka.
      const title = track === null ? station.name : track.title;
      const artist = track === null ? null : track.artist;

      const key = signature(title, artist, state.artworkUrl);
      if (key === last) return;
      last = key;

      session.setNowPlaying({
        title,
        ...(artist === null ? {} : { artist }),
        // Ime postaje pripada albumu samo takrat, kadar je naslov zaseden s
        // skladbo — sicer bi CarPlay isto besedilo pokazal dvakrat. Isto
        // pravilo kot podnapis v `ui/components/nowplaying.ts`.
        ...(track === null ? {} : { album: station.name }),
        ...(state.artworkUrl === null ? {} : { artworkUrl: state.artworkUrl }),
      });
    },
  };
}
